import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, TestSession, Question } from '../types';
import { isCorrectWrongReview, saveCorrectReviews } from '../utils/wrongReviewProgress';
import { normalizeStoredCscaSubject } from '../utils/cscaSubjectNumbering';

interface AuthState {
  user: User | null;
  token: string | null;
  loginAt: number | null;
  isAuthenticated: boolean;
  login: (user: User, token: string) => void;
  setLoginAt: (loginAt: number) => void;
  logout: () => void;
}

interface TestState {
  currentTest: {
    level: number;
    part: string;
    questions: Question[];
    testId?: string;
    currentQuestionIndex: number;
    answers: (number | null)[];
	examScope?: string;
  } | null;
  testHistory: TestSession[];
  startTest: (level: number, part: string, questions: Question[], testId?: string, initialIndex?: number, initialAnswers?: (number | null)[], examScope?: string) => void;
  answerQuestion: (answerIndex: number) => void;
  nextQuestion: () => void;
  previousQuestion: () => void;
	goToQuestion: (index: number) => void;
  completeTest: () => TestSession | null;
  clearCurrentTest: () => void;
  addTestToHistory: (session: TestSession) => void;
}

export type SystemLanguage = 'zh' | 'en' | 'id';

interface SettingsState {
  systemLanguage: SystemLanguage;
  setSystemLanguage: (language: SystemLanguage) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      loginAt: null,
      isAuthenticated: false,
      login: (user, token) => set({ user, token, loginAt: Date.now(), isAuthenticated: true }),
      setLoginAt: (loginAt) => set({ loginAt }),
      logout: () => set({ user: null, token: null, loginAt: null, isAuthenticated: false }),
    }),
    {
      name: 'auth-storage', // unique name for localStorage key
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        loginAt: state.loginAt,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
);

export const useTestStore = create<TestState>()(
  persist(
    (set, get) => ({
      currentTest: null,
      testHistory: [],
      startTest: (level, part, questions, testId, initialIndex = 0, initialAnswers, examScope) => set({
        currentTest: {
          level,
          part,
          questions,
          testId,
          currentQuestionIndex: initialIndex,
          answers: initialAnswers || new Array(questions.length).fill(null),
		  examScope,
        }
      }),
      answerQuestion: (answerIndex) => {
        const state = get();
        if (!state.currentTest) return;
        const newAnswers = [...state.currentTest.answers];
        newAnswers[state.currentTest.currentQuestionIndex] = answerIndex;
        set({
          currentTest: {
            ...state.currentTest,
            answers: newAnswers,
          }
        });
      },
      nextQuestion: () => {
        const state = get();
        if (!state.currentTest) return;
        set({
          currentTest: {
            ...state.currentTest,
            currentQuestionIndex: Math.min(
              state.currentTest.currentQuestionIndex + 1,
              state.currentTest.questions.length - 1
            ),
          }
        });
      },
      previousQuestion: () => {
        const state = get();
        if (!state.currentTest) return;
        set({
          currentTest: {
            ...state.currentTest,
            currentQuestionIndex: Math.max(
              state.currentTest.currentQuestionIndex - 1,
              0
            ),
          }
        });
      },
	  goToQuestion: (index) => {
		const state = get();
		if (!state.currentTest) return;
		set({ currentTest: { ...state.currentTest, currentQuestionIndex: Math.max(0, Math.min(index, state.currentTest.questions.length - 1)) } });
	  },
      completeTest: () => {
        const state = get();
        if (!state.currentTest) return null;
        const score = state.currentTest.answers.reduce((acc, answer, index) => {
          if (answer === state.currentTest!.questions[index].correctAnswer) {
            return (acc || 0) + 1;
          }
          return acc || 0;
        }, 0);
        const session: TestSession = {
          id: state.currentTest.testId || crypto.randomUUID(),
          userId: '',
          level: state.currentTest.level,
          part: state.currentTest.part,
          questions: state.currentTest.questions,
          answers: state.currentTest.answers,
          score: score || 0,
          completedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        };
        set({ currentTest: null });
        return session;
      },
      clearCurrentTest: () => set({ currentTest: null }),
      addTestToHistory: (session) => {
        if (session.id.startsWith('practice-')) {
          const userId = useAuthStore.getState().user?.id;
          if (userId) saveCorrectReviews(userId, session.questions.filter((q, i) => isCorrectWrongReview(q, session.answers[i])).map(q => q.wrongReviewSource));
        }
        set((state) => ({
        testHistory: [
          ...state.testHistory.filter((item) => item.id !== session.id),
          session,
        ],
      }));
      },
    }),
    {
      name: 'test-storage',
      merge: (persisted, current) => {
        const saved = persisted as Partial<TestState> | undefined;
        return {
          ...current,
          ...saved,
          currentTest: saved?.currentTest ? normalizeStoredCscaSubject(saved.currentTest) : null,
          testHistory: (saved?.testHistory || []).map(normalizeStoredCscaSubject),
        };
      },
      partialize: (state) => ({
        currentTest: state.currentTest,
        testHistory: state.testHistory
      })
    }
  )
);

export function clearExamClientState() {
  useTestStore.setState({ currentTest: null, testHistory: [] });
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem('test-storage');
    window.localStorage.removeItem('pending-submissions');
    const keysToRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key && key.startsWith('simulation-writing-answers:')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    // Best effort only; logout should still proceed if storage is unavailable.
  }
}

async function clearBrowserSiteData() {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.clear();
  } catch {}

  try {
    window.sessionStorage.clear();
  } catch {}

  try {
    if (typeof document !== 'undefined' && document.cookie) {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const eqIndex = cookie.indexOf('=');
        const rawName = eqIndex >= 0 ? cookie.slice(0, eqIndex) : cookie;
        const name = rawName.trim();
        if (!name) continue;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
      }
    }
  } catch {}

  try {
    if ('caches' in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
    }
  } catch {}

  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {}

  try {
    const idbFactory = window.indexedDB as IDBFactory & {
      databases?: () => Promise<Array<{ name?: string | null }> | undefined>;
    };
    if (typeof idbFactory?.databases === 'function') {
      const databases = (await idbFactory.databases()) || [];
      await Promise.all(
        databases
          .map((database) => database?.name)
          .filter((name): name is string => typeof name === 'string' && name.length > 0)
          .map(
            (name) =>
              new Promise<void>((resolve) => {
                const request = window.indexedDB.deleteDatabase(name);
                request.onsuccess = () => resolve();
                request.onerror = () => resolve();
                request.onblocked = () => resolve();
              })
          )
      );
    }
  } catch {}
}

export async function performHardLogout(options?: { redirectToLogin?: boolean }) {
  useAuthStore.setState({ user: null, token: null, loginAt: null, isAuthenticated: false });
  useTestStore.setState({ currentTest: null, testHistory: [] });
  useSettingsStore.setState({ systemLanguage: 'zh' });

  await clearBrowserSiteData();

  if (options?.redirectToLogin && typeof window !== 'undefined') {
    window.location.replace('/login');
  }
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      systemLanguage: 'zh',
      setSystemLanguage: (systemLanguage) => set({ systemLanguage }),
    }),
    {
      name: 'settings-storage',
      partialize: (state) => ({
        systemLanguage: state.systemLanguage,
      }),
    }
  )
);
